import * as AT from 'redux/actionTypes';
import browserStorage from 'store';
import { uuidv4 } from 'utils/helpers';
import AuthService, { Auth0Profile, AuthSession, PkceLoginResult } from './AuthService';
import { clearAuthSessionMarker, getOrCreateAuthSessionMarker } from './authSessionMarker';
import { LOCAL_WORK_BACKUP_FAILED_REASON, PKCE_CALLBACK_FAILED_REASON, STORAGE_QUOTA_REASON } from './authStoragePrune';
import { AuthTelemetryData, AuthTelemetryEventName, emitAuthTelemetry } from './authTelemetry';
import { pauseAuthenticatedRequests } from './authenticatedRequestGate';
import { createGenerationNotifier } from './sessionCoordinatorChannel';
import { withRenewalLock } from './sessionCoordinatorLock';
import {
  clearGenerationRecord,
  isNewerUnexpiredGeneration,
  readGenerationRecord,
  SessionStorageLike,
  StoredGenerationRecord,
  writeGenerationRecord,
} from './sessionGenerationStore';
import { awaitLocalWorkBackup } from './sessionTermination';
import { AUTH_SESSION_STATUS, AuthGenerationRecord, AuthRenewalTrigger, AuthSessionStatus } from './sessionTypes';
import { deriveSessionTiming, INVALID_TOKEN_RESPONSE_ERROR_CODE, INVALID_TOKEN_RESPONSE_MESSAGE } from './tokenClaims';

export const TOKEN_RENEWAL_LEAD_TIME_MS = 10 * 60 * 1000;
export const TOKEN_RENEWAL_RETRY_DELAYS_MS = [10 * 1000, 30 * 1000, 60 * 1000];

const TERMINAL_RENEWAL_ERRORS = new Set([
  'consent_required',
  'interaction_required',
  'invalid_grant',
  INVALID_TOKEN_RESPONSE_ERROR_CODE,
  'login_required',
  'missing_refresh_token',
]);

export interface SessionCoordinatorAuthService {
  restoreCachedSession(): Promise<AuthSession | null>;
  forceRefreshSession(): Promise<AuthSession>;
  completePkceLogin(): Promise<PkceLoginResult>;
  isPkceCallback(): boolean;
  getUserInfo(): Promise<Auth0Profile>;
  login(): Promise<void>;
  logout(): Promise<void>;
  pruneLeftoverAuthStorage?: () => void;
}

export interface SessionCoordinatorClock {
  now(): number;
  setTimeout(callback: () => void, ms: number): ReturnType<typeof setTimeout>;
  clearTimeout(timeoutId: ReturnType<typeof setTimeout> | null): void;
}

export interface SessionCoordinatorDependencies {
  authService?: SessionCoordinatorAuthService;
  storage?: SessionStorageLike;
  createGenerationId?: () => string;
  createAttemptId?: () => string;
  clock?: SessionCoordinatorClock;
  withLock?: typeof withRenewalLock;
  ownerId?: string;
  webLocks?: Pick<LockManager, 'request'> | null;
  notifier?: ReturnType<typeof createGenerationNotifier>;
  addWindowEventListener?: (type: string, listener: EventListener) => void;
  removeWindowEventListener?: (type: string, listener: EventListener) => void;
  addDocumentEventListener?: (type: string, listener: EventListener) => void;
  removeDocumentEventListener?: (type: string, listener: EventListener) => void;
  getVisibilityState?: () => DocumentVisibilityState | undefined;
  pauseAuthenticatedRequests?: () => Promise<(() => void) | void>;
  awaitLocalWorkBackup?: () => Promise<void>;
  emitTelemetry?: (event: AuthTelemetryEventName, data?: AuthTelemetryData) => void;
}

type CoordinatorDispatch = (action: { type: string; payload?: unknown }) => void;

type PendingAuthenticationOperation =
  | {
      kind: 'redirectToLogin';
      reason: string;
      clearInconsistentAppSession: boolean;
    }
  | {
      kind: 'logout';
    };

export type AuthenticationRetryIntent = 'signIn' | 'logout';

export interface SessionCoordinatorSnapshot {
  sessionStatus: AuthSessionStatus;
  authenticationReason: string | null;
  retryIntent: AuthenticationRetryIntent | null;
}

const defaultClock: SessionCoordinatorClock = {
  now: () => Date.now(),
  setTimeout: (callback, ms) => setTimeout(callback, ms),
  clearTimeout: (timeoutId) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  },
};

const getAuthErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const maybeError = error as { error?: unknown; code?: unknown };
  if (typeof maybeError.error === 'string') {
    return maybeError.error;
  }
  if (typeof maybeError.code === 'string') {
    return maybeError.code;
  }
  return undefined;
};

const isTerminalRenewalError = (error: unknown): boolean => TERMINAL_RENEWAL_ERRORS.has(getAuthErrorCode(error) ?? '');

const toGenerationRecord = (
  session: AuthSession,
  profile: Auth0Profile | null,
  generationId: string,
  now: number
): AuthGenerationRecord => {
  const timing = deriveSessionTiming(session.accessToken, now);
  return {
    accessToken: session.accessToken,
    scope: session.scope,
    profile,
    issuedAt: session.issuedAt ?? timing.issuedAt,
    expiresAt: timing.expiresAt,
    generationId,
  };
};

class SessionCoordinator {
  private readonly authService: SessionCoordinatorAuthService;

  private readonly storage: SessionStorageLike;

  private readonly createGenerationId: () => string;

  private readonly createAttemptId: () => string;

  private readonly clock: SessionCoordinatorClock;

  private readonly withLock: typeof withRenewalLock;

  private readonly ownerId: string;

  private readonly webLocks: Pick<LockManager, 'request'> | null | undefined;

  private readonly notifier: ReturnType<typeof createGenerationNotifier>;

  private readonly addWindowEventListener: (type: string, listener: EventListener) => void;

  private readonly removeWindowEventListener: (type: string, listener: EventListener) => void;

  private readonly addDocumentEventListener: (type: string, listener: EventListener) => void;

  private readonly removeDocumentEventListener: (type: string, listener: EventListener) => void;

  private readonly pauseAuthenticatedRequests: () => Promise<(() => void) | void>;

  private readonly awaitLocalWorkBackup: () => Promise<void>;

  private readonly getVisibilityState: () => DocumentVisibilityState | undefined;

  private readonly emitTelemetry: (event: AuthTelemetryEventName, data?: AuthTelemetryData) => void;

  private currentRecord: AuthGenerationRecord | null = null;

  private sessionStatus: AuthSessionStatus = AUTH_SESSION_STATUS.bootstrapping;

  private authenticationReason: string | null = null;

  private sessionSnapshot: SessionCoordinatorSnapshot = {
    sessionStatus: AUTH_SESSION_STATUS.bootstrapping,
    authenticationReason: null,
    retryIntent: null,
  };

  private dispatch: CoordinatorDispatch | null = null;

  private inTabRenewalPromise: Promise<AuthGenerationRecord> | null = null;

  private renewalAttemptId: string | null = null;

  private resumeCoordinationPromise: Promise<AuthGenerationRecord | null> | null = null;

  private tokenRenewalTimeout: ReturnType<typeof setTimeout> | null = null;

  private scheduledGenerationId: string | null = null;

  private reauthenticationPromise: Promise<void> | null = null;

  private explicitLogoutPromise: Promise<boolean> | null = null;

  private authenticationRetryPromise: Promise<void> | null = null;

  private pendingAuthenticationOperation: PendingAuthenticationOperation | null = null;

  private readonly statusListeners = new Set<() => void>();

  private listenersRegistered = false;

  private unsubscribeNotifications: (() => void) | null = null;

  constructor(dependencies: SessionCoordinatorDependencies = {}) {
    this.authService = dependencies.authService ?? AuthService;
    this.storage = dependencies.storage ?? browserStorage;
    this.createGenerationId = dependencies.createGenerationId ?? uuidv4;
    this.createAttemptId = dependencies.createAttemptId ?? uuidv4;
    this.clock = dependencies.clock ?? defaultClock;
    this.withLock = dependencies.withLock ?? withRenewalLock;
    this.ownerId = dependencies.ownerId ?? uuidv4();
    this.webLocks = dependencies.webLocks;
    this.notifier = dependencies.notifier ?? createGenerationNotifier();
    this.addWindowEventListener =
      dependencies.addWindowEventListener ?? ((type, listener) => window.addEventListener(type, listener));
    this.removeWindowEventListener =
      dependencies.removeWindowEventListener ?? ((type, listener) => window.removeEventListener(type, listener));
    this.addDocumentEventListener =
      dependencies.addDocumentEventListener ?? ((type, listener) => document.addEventListener(type, listener));
    this.removeDocumentEventListener =
      dependencies.removeDocumentEventListener ?? ((type, listener) => document.removeEventListener(type, listener));
    this.getVisibilityState = dependencies.getVisibilityState ?? (() => document.visibilityState);
    this.pauseAuthenticatedRequests =
      dependencies.pauseAuthenticatedRequests ?? (async () => pauseAuthenticatedRequests());
    this.awaitLocalWorkBackup = dependencies.awaitLocalWorkBackup ?? awaitLocalWorkBackup;
    this.emitTelemetry = dependencies.emitTelemetry ?? emitAuthTelemetry;
  }

  bindDispatch(dispatch: CoordinatorDispatch) {
    this.dispatch = dispatch;
  }

  getAuthenticationReason(): string | null {
    return this.authenticationReason;
  }

  getSessionSnapshot(): SessionCoordinatorSnapshot {
    return this.sessionSnapshot;
  }

  subscribe(listener: () => void): () => void {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  getCurrentGeneration(): AuthGenerationRecord | null {
    return this.currentRecord;
  }

  getSessionStatus(): AuthSessionStatus {
    return this.sessionStatus;
  }

  async waitForResumeCoordination(): Promise<AuthGenerationRecord | null> {
    if (this.resumeCoordinationPromise) {
      return this.resumeCoordinationPromise;
    }
    if (this.inTabRenewalPromise) {
      return this.inTabRenewalPromise;
    }
    return this.currentRecord;
  }

  onLoginSuccess(payload: {
    accessToken: string;
    expiresAt: number;
    scope: string;
    profile: Auth0Profile;
    issuedAt?: number;
    generationId?: string;
  }): AuthGenerationRecord {
    this.pendingAuthenticationOperation = null;
    const existing = this.currentRecord;
    if (
      existing &&
      existing.accessToken === payload.accessToken &&
      (payload.generationId == null || payload.generationId === existing.generationId)
    ) {
      this.setStatus(AUTH_SESSION_STATUS.authenticated, null);
      this.scheduleRenewal(existing);
      this.registerSessionResumeListeners();
      return existing;
    }

    const record = toGenerationRecord(
      {
        accessToken: payload.accessToken,
        expiresAt: payload.expiresAt,
        scope: payload.scope,
        issuedAt: payload.issuedAt,
      },
      payload.profile,
      payload.generationId ?? existing?.generationId ?? this.createGenerationId(),
      this.clock.now()
    );
    this.persistGeneration(record);
    this.setStatus(AUTH_SESSION_STATUS.authenticated, null);
    this.scheduleRenewal(record);
    this.registerSessionResumeListeners();
    return record;
  }

  async bootstrap(_existingProfile?: Auth0Profile | null) {
    this.setStatus(AUTH_SESSION_STATUS.bootstrapping, null);
    this.registerSessionResumeListeners();

    if (this.authService.isPkceCallback()) {
      try {
        const { profile, returnTo, session } = await this.authService.completePkceLogin();

        // Auth0 requires applications with custom routers to perform the post-login navigation (even when using the React library)
        // https://auth0.com/docs/libraries/auth0-react#protect-a-route
        window.history.replaceState({}, document.title, `${window.location.pathname}${returnTo}`);
        // replaceState cleans the callback URL without emitting popstate, so notify the mounted HashRouter explicitly:
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/popstate_event
        // https://reactrouter.com/6.30.1/router-components/hash-router
        window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
        const record = toGenerationRecord(session, profile, this.createGenerationId(), this.clock.now());
        clearAuthSessionMarker(this.storage);
        this.persistGeneration(record);
        this.setStatus(AUTH_SESSION_STATUS.authenticated, null);
        this.scheduleRenewal(record);
        this.publishAuthenticated(record, true);
        return record;
      } catch (error) {
        console.error('Unable to complete Auth0 PKCE login', error);
        window.history.replaceState({}, document.title, window.location.pathname);
        const reason =
          getAuthErrorCode(error) === STORAGE_QUOTA_REASON
            ? `${PKCE_CALLBACK_FAILED_REASON}:${STORAGE_QUOTA_REASON}`
            : PKCE_CALLBACK_FAILED_REASON;
        this.emitTelemetry(
          reason.includes(STORAGE_QUOTA_REASON) ? 'auth.pkce.storage_quota' : 'auth.pkce.callback_failed',
          { reason }
        );
        if (reason.includes(STORAGE_QUOTA_REASON)) {
          this.authService.pruneLeftoverAuthStorage?.();
        }
        this.enterAuthenticationError(reason, {
          kind: 'redirectToLogin',
          reason,
          clearInconsistentAppSession: false,
        });
        return null;
      }
    }

    const storedBeforeRestore = readGenerationRecord(this.storage);
    if (!storedBeforeRestore) {
      this.enterUnauthenticatedBootstrap();
      return null;
    }

    try {
      const restored = await this.authService.restoreCachedSession();
      if (!restored) {
        await this.migrateUnrestorableStoredSession('missing_refresh_token');
        return null;
      }

      const now = this.clock.now();
      const sharedAfterRestore = readGenerationRecord(this.storage);
      const profile = await this.authService.getUserInfo();
      const matchingStoredRecord =
        sharedAfterRestore?.accessToken === restored.accessToken ? sharedAfterRestore : storedBeforeRestore;
      const restoredRecord = toGenerationRecord(
        restored,
        profile,
        matchingStoredRecord.accessToken === restored.accessToken && matchingStoredRecord.generationId
          ? matchingStoredRecord.generationId
          : this.createGenerationId(),
        now
      );
      const record =
        sharedAfterRestore && isNewerUnexpiredGeneration(sharedAfterRestore, restoredRecord, now)
          ? {
              ...sharedAfterRestore,
              profile,
              issuedAt: sharedAfterRestore.issuedAt ?? now,
              generationId: sharedAfterRestore.generationId as string,
            }
          : restoredRecord;

      if (record.expiresAt <= now) {
        const renewedRecord = await this.renew('bootstrap');
        this.publishAuthenticated(renewedRecord, true);
        return renewedRecord;
      } else {
        this.persistGeneration(record);
        this.setStatus(AUTH_SESSION_STATUS.authenticated, null);
        this.scheduleRenewal(record);
        this.publishAuthenticated(record, true);
      }

      return record;
    } catch (error) {
      console.error('Unable to restore the stored Auth0 session', error);
      if (isTerminalRenewalError(error) || getAuthErrorCode(error) === 'missing_refresh_token') {
        await this.migrateUnrestorableStoredSession(getAuthErrorCode(error) ?? 'missing_refresh_token');
        return null;
      }
      await this.migrateUnrestorableStoredSession(getAuthErrorCode(error) ?? 'unrestorable_session');
      return null;
    }
  }

  async renew(trigger: AuthRenewalTrigger = 'manual'): Promise<AuthGenerationRecord> {
    if (!this.inTabRenewalPromise) {
      const attemptId = this.createAttemptId();
      const startedAt = this.clock.now();
      this.renewalAttemptId = attemptId;
      this.emitRenewalTelemetry('auth.renewal.started', attemptId, trigger);
      const renewalPromise = this.runCoordinatedRenewal(trigger, attemptId);
      void renewalPromise.then(
        (record) => {
          this.emitRenewalTelemetry('auth.renewal.succeeded', attemptId, trigger, {
            durationMs: this.clock.now() - startedAt,
            generationId: record.generationId,
          });
        },
        (error) => {
          this.emitRenewalTelemetry('auth.renewal.failed', attemptId, trigger, {
            durationMs: this.clock.now() - startedAt,
            reason: getAuthErrorCode(error) ?? 'renewal_failed',
          });
        }
      );
      this.inTabRenewalPromise = renewalPromise.finally(() => {
        this.inTabRenewalPromise = null;
        this.renewalAttemptId = null;
      });
    } else if (this.renewalAttemptId) {
      this.emitRenewalTelemetry('auth.renewal.joined', this.renewalAttemptId, trigger);
    }

    return this.inTabRenewalPromise;
  }

  async recoverAuthenticationSession(): Promise<AuthGenerationRecord | null> {
    try {
      await this.waitForResumeCoordination();
      return await this.renew('401');
    } catch (error) {
      const reason = getAuthErrorCode(error) ?? 'recovery_failed';
      if (isTerminalRenewalError(error)) {
        console.error('Auth0 token recovery failed; requiring reauthentication', error);
        this.enterReauthenticationRequired(reason);
      } else {
        console.warn('Auth0 token recovery failed; preserving the current session', error);
        this.setStatus(AUTH_SESSION_STATUS.degraded, reason);
        this.dispatch?.({
          type: AT.AUTH_SESSION_DEGRADED,
          payload: { reason },
        });
      }
      return null;
    }
  }

  requireReauthentication(reason: string) {
    void this.startControlledReauthentication(reason);
  }

  retryAuthentication(): Promise<void> {
    if (this.authenticationRetryPromise) {
      return this.authenticationRetryPromise;
    }

    if (this.sessionStatus !== AUTH_SESSION_STATUS.authenticationError) {
      return Promise.resolve();
    }

    const operation = this.pendingAuthenticationOperation ?? {
      kind: 'redirectToLogin',
      reason: this.authenticationReason ?? 'retry',
      clearInconsistentAppSession: false,
    };

    this.authenticationRetryPromise = this.retryPendingAuthenticationOperation(operation).finally(() => {
      this.authenticationRetryPromise = null;
    });
    return this.authenticationRetryPromise;
  }

  handleExplicitLogout() {
    if (!this.explicitLogoutPromise) {
      this.explicitLogoutPromise = this.runExplicitLogout().finally(() => {
        this.explicitLogoutPromise = null;
      });
    }

    return this.explicitLogoutPromise;
  }

  private async runCoordinatedRenewal(trigger: AuthRenewalTrigger, attemptId: string): Promise<AuthGenerationRecord> {
    const now = this.clock.now();
    const currentRecord = this.currentRecord ?? readGenerationRecord(this.storage);
    const sharedBeforeLock = readGenerationRecord(this.storage);
    if (sharedBeforeLock && isNewerUnexpiredGeneration(sharedBeforeLock, currentRecord, now)) {
      return this.adoptSharedGeneration(sharedBeforeLock, 'pre_lock', attemptId, trigger);
    }

    this.setStatus(AUTH_SESSION_STATUS.renewing, null);

    return this.withLock(
      async (assertLockOwnership = () => undefined) => {
        const sharedAfterLock = readGenerationRecord(this.storage);
        const nowAfterLock = this.clock.now();
        this.emitRenewalTelemetry('auth.renewal.lock', attemptId, trigger, {
          lockStage: 'post_lock_generation_compared',
          reason: sharedAfterLock?.generationId === currentRecord?.generationId ? 'unchanged' : 'changed',
        });
        if (sharedAfterLock && isNewerUnexpiredGeneration(sharedAfterLock, currentRecord, nowAfterLock)) {
          return this.adoptSharedGeneration(sharedAfterLock, 'post_lock', attemptId, trigger);
        }

        const restoredAfterLock = await this.authService.restoreCachedSession();
        if (restoredAfterLock && restoredAfterLock.accessToken !== currentRecord?.accessToken) {
          const sharedAfterRestore = readGenerationRecord(this.storage);
          if (sharedAfterRestore?.accessToken === restoredAfterLock.accessToken && sharedAfterRestore.generationId) {
            return this.adoptSharedGeneration(sharedAfterRestore, 'sdk_cache', attemptId, trigger);
          }

          const profile =
            this.currentRecord?.profile || sharedAfterRestore?.profile || (await this.getCurrentProfile());
          const restoredRecord = toGenerationRecord(
            restoredAfterLock,
            profile,
            this.createGenerationId(),
            this.clock.now()
          );
          assertLockOwnership();
          this.persistGeneration(restoredRecord);
          this.setStatus(AUTH_SESSION_STATUS.authenticated, 'adopted_sdk_cache');
          this.scheduleRenewal(restoredRecord);
          this.publishAuthenticated(restoredRecord, false);
          this.notifier.notifyGenerationUpdated(restoredRecord.generationId);
          this.emitRenewalTelemetry('auth.renewal.generation_published', attemptId, trigger, {
            generationId: restoredRecord.generationId,
            adoptedFrom: 'sdk_cache',
          });
          return restoredRecord;
        }

        this.emitRenewalTelemetry('auth.renewal.forced_exchange', attemptId, trigger, { lockStage: 'started' });
        const session = await this.authService.forceRefreshSession();
        this.emitRenewalTelemetry('auth.renewal.forced_exchange', attemptId, trigger, { lockStage: 'completed' });
        const profile = this.currentRecord?.profile || sharedAfterLock?.profile || (await this.getCurrentProfile());
        const record = toGenerationRecord(session, profile, this.createGenerationId(), nowAfterLock);
        assertLockOwnership();
        this.persistGeneration(record);
        this.setStatus(AUTH_SESSION_STATUS.authenticated, null);
        this.scheduleRenewal(record);
        this.publishAuthenticated(record, false);
        this.notifier.notifyGenerationUpdated(record.generationId);
        this.emitRenewalTelemetry('auth.renewal.generation_published', attemptId, trigger, {
          generationId: record.generationId,
        });
        return record;
      },
      {
        ownerId: this.ownerId,
        storage: this.storage,
        now: () => this.clock.now(),
        webLocks: this.webLocks,
        onLockStage: (lockStage, lockMechanism) => {
          this.emitRenewalTelemetry('auth.renewal.lock', attemptId, trigger, { lockStage, lockMechanism });
        },
      }
    );
  }

  private persistGeneration(record: AuthGenerationRecord) {
    getOrCreateAuthSessionMarker(this.storage);
    writeGenerationRecord(record, this.storage);
    this.currentRecord = record;
  }

  private adoptSharedGeneration(
    stored: StoredGenerationRecord,
    reason: 'pre_lock' | 'post_lock' | 'sdk_cache',
    attemptId?: string,
    trigger?: AuthRenewalTrigger
  ): AuthGenerationRecord {
    const now = this.clock.now();
    const record: AuthGenerationRecord = {
      accessToken: stored.accessToken,
      scope: stored.scope,
      profile: stored.profile || this.currentRecord?.profile || null,
      issuedAt: stored.issuedAt ?? now,
      expiresAt: stored.expiresAt,
      generationId: stored.generationId as string,
    };
    this.currentRecord = record;
    this.setStatus(AUTH_SESSION_STATUS.authenticated, `adopted_${reason}`);
    this.scheduleRenewal(record);
    this.publishAuthenticated(record, false);
    if (attemptId && trigger) {
      this.emitRenewalTelemetry('auth.renewal.adopted_generation', attemptId, trigger, {
        generationId: record.generationId,
        adoptedFrom: reason,
      });
    }
    return record;
  }

  private emitRenewalTelemetry(
    event: AuthTelemetryEventName,
    attemptId: string,
    trigger: AuthRenewalTrigger,
    data: AuthTelemetryData = {}
  ) {
    const generation = this.currentRecord;
    this.emitTelemetry(event, {
      attemptId,
      generationId: generation?.generationId ?? null,
      trigger,
      remainingLifetimeMs: generation ? generation.expiresAt - this.clock.now() : null,
      visibilityState: this.getVisibilityState() ?? null,
      online: typeof navigator === 'undefined' ? null : navigator.onLine,
      ...data,
    });
  }

  private scheduleRenewal(record: AuthGenerationRecord) {
    this.clearScheduledRenewal();
    this.scheduledGenerationId = record.generationId;
    const delay = Math.max(0, record.expiresAt - this.clock.now() - TOKEN_RENEWAL_LEAD_TIME_MS);
    this.tokenRenewalTimeout = this.clock.setTimeout(() => {
      this.tokenRenewalTimeout = null;
      if (this.scheduledGenerationId !== this.currentRecord?.generationId) {
        return;
      }
      void this.renewFromTimer(record);
    }, delay);
  }

  private async renewFromTimer(scheduledRecord: AuthGenerationRecord, retryAttempt = 0) {
    if (scheduledRecord.generationId !== this.currentRecord?.generationId) {
      return;
    }

    try {
      await this.renew('timer');
    } catch (error) {
      if (scheduledRecord.generationId !== this.currentRecord?.generationId) {
        return;
      }

      const retryDelay = TOKEN_RENEWAL_RETRY_DELAYS_MS[retryAttempt];
      const canRetryBeforeExpiry = retryDelay && this.clock.now() + retryDelay < scheduledRecord.expiresAt;

      if (!isTerminalRenewalError(error) && canRetryBeforeExpiry) {
        console.warn(`Auth0 token renewal failed; retrying in ${retryDelay}ms`, error);
        this.setStatus(AUTH_SESSION_STATUS.degraded, getAuthErrorCode(error) ?? 'transient_renewal_failure');
        this.dispatch?.({
          type: AT.AUTH_SESSION_DEGRADED,
          payload: { reason: getAuthErrorCode(error) ?? 'transient_renewal_failure' },
        });
        this.tokenRenewalTimeout = this.clock.setTimeout(() => {
          this.tokenRenewalTimeout = null;
          void this.renewFromTimer(scheduledRecord, retryAttempt + 1);
        }, retryDelay);
        return;
      }

      if (isTerminalRenewalError(error)) {
        console.error('Auth0 token renewal failed; requiring reauthentication', error);
        this.enterReauthenticationRequired(getAuthErrorCode(error) ?? 'renewal_failed');
        return;
      }

      console.warn('Auth0 token renewal failed; remaining in degraded state', error);
      this.setStatus(AUTH_SESSION_STATUS.degraded, getAuthErrorCode(error) ?? 'transient_renewal_exhausted');
      this.dispatch?.({
        type: AT.AUTH_SESSION_DEGRADED,
        payload: { reason: getAuthErrorCode(error) ?? 'transient_renewal_exhausted' },
      });
    }
  }

  private handleSessionResume = () => {
    const visibilityState = this.getVisibilityState();
    if (visibilityState && visibilityState !== 'visible') {
      return;
    }

    const record = this.currentRecord;
    if (!record) {
      return;
    }

    if (record.expiresAt - this.clock.now() > TOKEN_RENEWAL_LEAD_TIME_MS) {
      return;
    }

    this.clearScheduledRenewal();
    this.resumeCoordinationPromise = this.renew('focus')
      .catch((error) => {
        if (isTerminalRenewalError(error)) {
          this.enterReauthenticationRequired(getAuthErrorCode(error) ?? 'resume_failed');
          return null;
        }
        this.setStatus(AUTH_SESSION_STATUS.degraded, getAuthErrorCode(error) ?? 'resume_transient_failure');
        this.dispatch?.({
          type: AT.AUTH_SESSION_DEGRADED,
          payload: { reason: getAuthErrorCode(error) ?? 'resume_transient_failure' },
        });
        return this.currentRecord;
      })
      .finally(() => {
        this.resumeCoordinationPromise = null;
      });
  };

  private handleOnline = () => {
    this.handleSessionResume();
  };

  private handleGenerationNotice = () => {
    const stored = readGenerationRecord(this.storage);
    const now = this.clock.now();
    if (isNewerUnexpiredGeneration(stored, this.currentRecord, now)) {
      this.adoptSharedGeneration(stored, 'post_lock');
    }
  };

  private registerSessionResumeListeners() {
    if (this.listenersRegistered) {
      return;
    }
    this.addWindowEventListener('focus', this.handleSessionResume);
    this.addWindowEventListener('online', this.handleOnline);
    this.addDocumentEventListener('visibilitychange', this.handleSessionResume);
    this.unsubscribeNotifications = this.notifier.subscribeToGenerationNotifications(this.handleGenerationNotice);
    this.listenersRegistered = true;
  }

  private unregisterSessionResumeListeners() {
    if (!this.listenersRegistered) {
      return;
    }
    this.removeWindowEventListener('focus', this.handleSessionResume);
    this.removeWindowEventListener('online', this.handleOnline);
    this.removeDocumentEventListener('visibilitychange', this.handleSessionResume);
    this.unsubscribeNotifications?.();
    this.unsubscribeNotifications = null;
    this.listenersRegistered = false;
  }

  private clearScheduledRenewal() {
    this.clock.clearTimeout(this.tokenRenewalTimeout);
    this.tokenRenewalTimeout = null;
    this.scheduledGenerationId = null;
  }

  private async getCurrentProfile(): Promise<Auth0Profile | null> {
    return (this.storage.get('profile') as Auth0Profile | null) || this.authService.getUserInfo();
  }

  private publishAuthenticated(record: AuthGenerationRecord, isLogin: boolean) {
    if (!this.dispatch) {
      return;
    }

    if (isLogin) {
      this.dispatch({
        type: AT.LOGIN_SUCCESS,
        payload: {
          profile: record.profile,
          scope: record.scope,
          accessToken: record.accessToken,
          expiresAt: record.expiresAt,
          issuedAt: record.issuedAt,
          generationId: record.generationId,
          sessionStatus: AUTH_SESSION_STATUS.authenticated,
          authenticationReason: null,
        },
      });
      return;
    }

    this.dispatch({
      type: AT.RENEW_TOKEN,
      payload: {
        accessToken: record.accessToken,
        scope: record.scope,
        issuedAt: record.issuedAt,
        expiresAt: record.expiresAt,
        generationId: record.generationId,
      },
    });
  }

  private setStatus(status: AuthSessionStatus, reason: string | null) {
    let retryIntent: AuthenticationRetryIntent | null = null;
    if (this.pendingAuthenticationOperation?.kind === 'logout') {
      retryIntent = 'logout';
    } else if (this.pendingAuthenticationOperation?.kind === 'redirectToLogin') {
      retryIntent = 'signIn';
    }

    this.sessionStatus = status;
    this.authenticationReason = reason;
    this.sessionSnapshot = {
      sessionStatus: status,
      authenticationReason: reason,
      retryIntent,
    };
    this.statusListeners.forEach((listener) => listener());
  }

  private enterReauthenticationRequired(reason: string) {
    void this.startControlledReauthentication(reason);
  }

  private startControlledReauthentication(reason: string) {
    if (
      this.sessionStatus === AUTH_SESSION_STATUS.redirectingToLogin ||
      this.sessionStatus === AUTH_SESSION_STATUS.loggingOut ||
      this.sessionStatus === AUTH_SESSION_STATUS.authenticationError
    ) {
      return this.reauthenticationPromise;
    }

    if (!this.reauthenticationPromise) {
      this.reauthenticationPromise = this.runControlledReauthentication(reason).finally(() => {
        this.reauthenticationPromise = null;
      });
    }

    return this.reauthenticationPromise;
  }

  private async runControlledReauthentication(reason: string) {
    this.emitTelemetry('auth.reauthentication.required', {
      generationId: this.currentRecord?.generationId ?? null,
      reason,
    });
    this.setStatus(AUTH_SESSION_STATUS.reauthenticationRequired, reason);
    this.dispatch?.({
      type: AT.AUTH_REAUTHENTICATION_REQUIRED,
      payload: { reason },
    });

    await this.redirectToLoginAfterBackup({
      kind: 'redirectToLogin',
      reason,
      clearInconsistentAppSession: true,
    });
  }

  private async migrateUnrestorableStoredSession(reason: string) {
    await this.redirectToLoginAfterBackup({
      kind: 'redirectToLogin',
      reason,
      clearInconsistentAppSession: true,
    });
  }

  private async retryPendingAuthenticationOperation(operation: PendingAuthenticationOperation): Promise<void> {
    if (operation.kind === 'logout') {
      await this.handleExplicitLogout();
      return;
    }

    if (operation.reason.includes(STORAGE_QUOTA_REASON)) {
      this.authService.pruneLeftoverAuthStorage?.();
    }

    await this.redirectToLoginAfterBackup(operation);
  }

  private async redirectToLoginAfterBackup(
    operation: Extract<PendingAuthenticationOperation, { kind: 'redirectToLogin' }>
  ) {
    let resumeAuthenticatedRequests: (() => void) | void;
    try {
      resumeAuthenticatedRequests = await this.pauseAuthenticatedRequests();
      await this.awaitLocalWorkBackup();
    } catch (error) {
      if (typeof resumeAuthenticatedRequests === 'function') {
        resumeAuthenticatedRequests();
      }
      this.emitTelemetry('auth.local_work_backup.failed', {
        generationId: this.currentRecord?.generationId ?? null,
        reason: LOCAL_WORK_BACKUP_FAILED_REASON,
      });
      console.error('Local-work backup failed; remaining in-app', error);
      this.enterAuthenticationError(LOCAL_WORK_BACKUP_FAILED_REASON, operation);
      return;
    }

    if (operation.clearInconsistentAppSession) {
      clearAuthSessionMarker(this.storage);
      clearGenerationRecord(this.storage);
      this.currentRecord = null;
    }

    this.pendingAuthenticationOperation = null;
    this.clearScheduledRenewal();
    this.setStatus(AUTH_SESSION_STATUS.redirectingToLogin, operation.reason);
    this.dispatch?.({
      type: AT.AUTH_SESSION_SNAPSHOT,
      payload: {
        profile: this.currentRecord?.profile ?? null,
        scope: this.currentRecord?.scope ?? null,
        accessToken: this.currentRecord?.accessToken ?? null,
        issuedAt: this.currentRecord?.issuedAt ?? null,
        expiresAt: this.currentRecord?.expiresAt ?? null,
        generationId: this.currentRecord?.generationId ?? null,
        sessionStatus: AUTH_SESSION_STATUS.redirectingToLogin,
        authenticationReason: operation.reason,
      },
    });

    try {
      await this.authService.login();
      this.pendingAuthenticationOperation = null;
    } catch (error) {
      if (typeof resumeAuthenticatedRequests === 'function') {
        resumeAuthenticatedRequests();
      }
      console.error('Unable to start Auth0 PKCE login', error);
      const reason = getAuthErrorCode(error) ?? PKCE_CALLBACK_FAILED_REASON;
      this.enterAuthenticationError(reason, { ...operation, reason });
    }
  }

  private async runExplicitLogout(): Promise<boolean> {
    const operation: PendingAuthenticationOperation = { kind: 'logout' };
    this.emitTelemetry('auth.logout.explicit', {
      generationId: this.currentRecord?.generationId ?? null,
      reason: 'explicit_logout',
    });
    this.pendingAuthenticationOperation = null;
    this.setStatus(AUTH_SESSION_STATUS.loggingOut, 'explicit_logout');
    this.dispatch?.({
      type: AT.AUTH_SESSION_SNAPSHOT,
      payload: {
        profile: this.currentRecord?.profile ?? null,
        scope: this.currentRecord?.scope ?? null,
        accessToken: this.currentRecord?.accessToken ?? null,
        issuedAt: this.currentRecord?.issuedAt ?? null,
        expiresAt: this.currentRecord?.expiresAt ?? null,
        generationId: this.currentRecord?.generationId ?? null,
        sessionStatus: AUTH_SESSION_STATUS.loggingOut,
        authenticationReason: 'explicit_logout',
      },
    });

    let resumeAuthenticatedRequests: (() => void) | void;
    try {
      resumeAuthenticatedRequests = await this.pauseAuthenticatedRequests();
      await this.awaitLocalWorkBackup();
    } catch (error) {
      if (typeof resumeAuthenticatedRequests === 'function') {
        resumeAuthenticatedRequests();
      }
      this.emitTelemetry('auth.local_work_backup.failed', {
        generationId: this.currentRecord?.generationId ?? null,
        reason: LOCAL_WORK_BACKUP_FAILED_REASON,
      });
      console.error('Local-work backup failed; remaining in-app', error);
      this.enterAuthenticationError(LOCAL_WORK_BACKUP_FAILED_REASON, operation);
      return false;
    }

    this.clearScheduledRenewal();
    this.unregisterSessionResumeListeners();
    clearAuthSessionMarker(this.storage);
    clearGenerationRecord(this.storage);
    this.currentRecord = null;

    try {
      await this.authService.logout();
    } catch (error) {
      console.error('Unable to complete Auth0 logout', error);
    }

    this.pendingAuthenticationOperation = null;
    return true;
  }

  private enterAuthenticationError(reason: string, operation: PendingAuthenticationOperation) {
    this.pendingAuthenticationOperation = operation;
    this.setStatus(AUTH_SESSION_STATUS.authenticationError, reason);
    this.dispatch?.({
      type: AT.AUTH_AUTHENTICATION_ERROR,
      payload: { reason },
    });
  }

  private enterUnauthenticatedBootstrap() {
    this.currentRecord = null;
    this.setStatus(AUTH_SESSION_STATUS.bootstrapping, null);
    this.dispatch?.({
      type: AT.FINISHED_TRY_AUTHENTICATE_FROM_LOCAL_STORAGE,
      payload: {},
    });
  }
}

export const createSessionCoordinator = (dependencies?: SessionCoordinatorDependencies) =>
  new SessionCoordinator(dependencies);

const sessionCoordinator = createSessionCoordinator();

export { INVALID_TOKEN_RESPONSE_MESSAGE };
export default sessionCoordinator;
